// Dashboard — KPI strip + GMV bar chart + queues + audit log + recent
// bookings. PR 2.7 wires this to /admin/dashboard via TanStack Query;
// the layout still mirrors the cardrive prototype.

import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  KPI,
  PageHeader,
  Sparkline,
  ToolBtn,
  Avatar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import {
  fetchAdminDashboard,
  fetchOverview,
  downloadDashboardCsv,
  type AdminOverviewResponse,
} from '@/lib/admin-api';
import { MoneyCard } from '@/components/MoneyCard';
import {
  PRESETS,
  defaultRange,
  formatPeriodLabel,
  periodProblem,
  presetRange,
  type MonthRange,
  type PeriodProblem,
  type PresetId,
} from '@/lib/month-period';

interface QueueTile {
  label: string;
  to: string;
  accent: string;
  read: (q: {
    pendingBookings: number;
    pendingCars: number;
    pendingReviews: number;
    flaggedReviews: number;
    pendingPayouts: number;
  }) => number;
}

const QUEUES: QueueTile[] = [
  {
    label: 'Listings to review',
    to: '/cars',
    accent: '#f59e0b',
    read: (q) => q.pendingCars,
  },
  {
    label: 'Booking requests',
    to: '/bookings',
    accent: '#3b82f6',
    read: (q) => q.pendingBookings,
  },
  {
    label: 'Reviews pending',
    to: '/reviews',
    accent: '#f59e0b',
    read: (q) => q.pendingReviews,
  },
  {
    label: 'Flagged reviews',
    to: '/reviews',
    accent: '#ef4444',
    read: (q) => q.flaggedReviews,
  },
  {
    label: 'Payouts to action',
    to: '/payouts',
    accent: '#10b981',
    read: (q) => q.pendingPayouts,
  },
];

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(v);

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

// ─── Overview ────────────────────────────────────────────────────────
//
// THE RULE THIS BLOCK EXISTS TO HOLD: money is never summed across
// currencies. The API hands back four measures — `revenue` (what customers
// paid), `drivecarsRevenue` (what we keep), `partnerCommissions` (what
// partners earned) and `supplierCommissions` (what crews owe us on a
// commission-model sale) — each as a LIST, one entry per currency, with no
// total field to print. Adding MAD to EUR would need a rate, and any rate we
// picked would make this screen read differently next year than it does today.
//
// So each MEASURE is a card and each currency is a ROW inside it. Grouping
// this way rather than one tile per (measure, currency) pair puts the
// comparison an operator actually makes — gross against net against
// commission, in one currency — across three cards at the same row position,
// and leaves nowhere for a merged figure to live.
//
// THE PERIOD. Everything derived from bookings above is bounded by the month
// window the control names; the stock counts (partners, crews, published
// cities and countries) are not, because those are facts about today rather
// than about a window. The caption says which window the money covers so the
// two kinds of tile are not read as answering the same question.

/** Why the Apply button is off, in one line. Named the same three ways the
 *  API names them, so the hint and a server 400 cannot describe the same
 *  window differently. */
const PROBLEM_HINT: Record<PeriodProblem, string> = {
  invalid_month: 'Pick a month for both From and To.',
  reversed: 'To must not be earlier than From.',
  too_long: 'A period may cover at most 12 months.',
};

export interface PeriodControlProps {
  /** The window currently APPLIED — what the figures on screen cover. */
  value: MonthRange;
  onApply: (range: MonthRange) => void;
}

/**
 * From / To months plus four presets.
 *
 * The draft is held apart from the applied window: typing in a month input
 * fires a change per keystroke, and refetching on each one would ask the
 * server for windows the operator is only passing through. Apply commits;
 * a preset commits immediately, because a preset is already a complete
 * choice rather than half of one.
 *
 * The guard is the client half of the API's rule. It disables Apply rather
 * than letting the request go and rendering the 400, so the operator learns
 * which rule they broke while they can still fix it in place.
 */
export function PeriodControl({ value, onApply }: PeriodControlProps) {
  const [draft, setDraft] = useState<MonthRange>(value);
  const problem = periodProblem(draft);
  const dirty = draft.from !== value.from || draft.to !== value.to;

  const apply = (range: MonthRange) => {
    setDraft(range);
    onApply(range);
  };

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-4">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
          From
        </span>
        <input
          type="month"
          aria-label="From"
          value={draft.from}
          onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          className="rounded-lg border border-[color:var(--bo-line)] px-2.5 py-1.5 text-[13px] tabular-nums"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
          To
        </span>
        <input
          type="month"
          aria-label="To"
          value={draft.to}
          onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          className="rounded-lg border border-[color:var(--bo-line)] px-2.5 py-1.5 text-[13px] tabular-nums"
        />
      </label>

      <button
        type="button"
        disabled={problem !== null || !dirty}
        onClick={() => onApply(draft)}
        className="rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-accent)] px-3 py-1.5 text-[13px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        Apply
      </button>

      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => apply(presetRange(preset.id as PresetId))}
            className="rounded-lg border border-[color:var(--bo-line)] px-2.5 py-1.5 text-[12px] hover:border-[color:var(--bo-mute)]"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {problem ? (
        <p role="status" className="basis-full text-[11.5px] text-[#b42318]">
          {PROBLEM_HINT[problem]}
        </p>
      ) : null}
    </div>
  );
}

export interface OverviewBlockProps {
  data: AdminOverviewResponse | undefined;
  isLoading: boolean;
  /** The window currently applied, for the caption. */
  period: MonthRange;
  /** Rendered above the tiles. Passed in rather than owned here so the block
   *  stays a pure view of a period someone else holds. */
  control?: ReactNode;
}

export function OverviewBlock({ data, isLoading, period, control }: OverviewBlockProps) {
  const n = (v: number | undefined) => (isLoading ? '…' : String(v ?? 0));
  const periodLabel = formatPeriodLabel(period);

  return (
    <section className="space-y-3">
      {control}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {/* Bookings and cancellations are OF THE PERIOD; the four counters
            after them are facts about today and say so, so a reader is never
            left guessing which tiles the control above moved. */}
        <KPI label="Bookings" value={n(data?.bookings)} hint={`confirmed + completed · ${periodLabel}`} />
        <KPI label="Cancelled" value={n(data?.cancelled)} hint={periodLabel} />
        <KPI label="Partners" value={n(data?.partners)} hint="active, third-party · today" />
        <KPI label="Crews" value={n(data?.crews)} hint="active, excl. suppliers · today" />
        <KPI label="Cities" value={n(data?.publishedCities)} hint="published · today" />
        <KPI label="Countries" value={n(data?.publishedCountries)} hint="published · today" />
      </div>

      {/* One card per MEASURE, one row per currency inside it. No total
          anywhere: see the note above. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MoneyCard
          title="Gross revenue"
          hint={`customers paid · ${periodLabel}`}
          rows={data?.revenue ?? []}
          isLoading={isLoading}
        />
        <MoneyCard
          title="DriveCars revenue"
          hint={`after crew, card + partner · ${periodLabel}`}
          rows={data?.drivecarsRevenue ?? []}
          isLoading={isLoading}
        />
        <MoneyCard
          title="Partner commissions"
          hint={`partners earned · ${periodLabel}`}
          rows={data?.partnerCommissions ?? []}
          isLoading={isLoading}
        />
        {/* 0241's fourth measure. It is money coming IN from CREWS — what they
            owe us on the base they collected in cash on a commission-model
            sale — so it is already folded into DriveCars revenue and appears
            in no customer's price. Beside the other three, never added to
            them. Empty until a partner is sold on Model 2 or 3, which the
            card's own sentence says rather than printing a zero. */}
        <MoneyCard
          title="Supplier commissions"
          hint={`crews owe us · ${periodLabel}`}
          rows={data?.supplierCommissions ?? []}
          isLoading={isLoading}
          emptyText="No commission-model bookings in this period"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GeoTable
          title="By country"
          isLoading={isLoading}
          rows={(data?.byCountry ?? []).map((r) => ({
            key: r.code,
            // A country row may itself be a draft, and a code with no name is
            // still a real place bookings happened in — print the code rather
            // than dropping the row.
            label: r.name ?? r.code.toUpperCase(),
            sub: r.code.toUpperCase(),
            bookings: r.bookings,
          }))}
        />
        <GeoTable
          title="By city"
          isLoading={isLoading}
          rows={(data?.byCity ?? []).map((r) => ({
            key: String(r.id),
            label: r.name ?? r.slug,
            sub: r.countryCode ? r.countryCode.toUpperCase() : '—',
            bookings: r.bookings,
          }))}
        />
      </div>

      {/* A total that silently shrank is worse than one that says why. Held
          back until the payload lands — a caption reading "0 excluded" before
          the count arrives is a claim, not a placeholder. */}
      {data ? (
        <p className="text-[11.5px] text-[color:var(--bo-mute)]">
          {data.testBookingsExcluded} test booking
          {data.testBookingsExcluded === 1 ? '' : 's'} excluded · {periodLabel}
        </p>
      ) : null}
    </section>
  );
}

interface GeoRow {
  key: string;
  label: string;
  sub: string;
  bookings: number;
}

/** Compact "where the bookings were" table — top 20, as the API caps it. */
function GeoTable({
  title,
  rows,
  isLoading,
}: {
  title: string;
  rows: GeoRow[];
  isLoading: boolean;
}) {
  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div className="text-[14px] font-semibold">{title}</div>
      <ul className="mt-3 divide-y divide-[color:var(--bo-line)]">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-3 py-2 text-[13px]">
            <div className="min-w-0 flex-1 truncate">{r.label}</div>
            <div className="shrink-0 text-[11.5px] text-[color:var(--bo-mute)]">{r.sub}</div>
            <div className="w-12 shrink-0 text-end tabular-nums">{r.bookings}</div>
          </li>
        ))}
        {!isLoading && rows.length === 0 ? (
          <li className="py-4 text-center text-[12px] text-[color:var(--bo-mute)]">
            No bookings yet.
          </li>
        ) : null}
      </ul>
    </div>
  );
}

export function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: ({ signal }) => fetchAdminDashboard(signal),
    refetchInterval: 60_000,
  });

  // The applied window. In the KEY, so switching period is a cache lookup
  // rather than a refetch of the same question, and two windows never share
  // an entry.
  const [period, setPeriod] = useState<MonthRange>(() => defaultRange());
  const overview = useQuery({
    queryKey: ['admin', 'overview', period.from, period.to],
    queryFn: ({ signal }) => fetchOverview(period, signal),
    refetchInterval: 60_000,
  });

  const kpis = data?.kpis;
  const queues = data?.queues ?? {
    pendingBookings: 0,
    pendingCars: 0,
    pendingReviews: 0,
    flaggedReviews: 0,
    pendingPayouts: 0,
  };
  const buckets = data?.gmvBuckets ?? [];
  const totalGmv = buckets.reduce((s, b) => s + b.total, 0);
  const totalBookings = buckets.reduce((s, b) => s + b.count, 0);
  const sparkValues = buckets.map((b) => b.total);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Overview"
        title="Marketplace today"
        sub="Live KPIs, queues that need a human, and the most recent bookings across the platform."
        actions={
          <>
            <ToolBtn icon="download" onClick={() => void downloadDashboardCsv()}>
              Export
            </ToolBtn>
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn't load dashboard: {(error as Error).message}
        </div>
      ) : null}

      {overview.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn't load overview: {(overview.error as Error).message}
        </div>
      ) : null}

      {/* Overview — period-bounded bookings + money per currency, today's
          stock counts, geography. */}
      <OverviewBlock
        data={overview.data}
        isLoading={overview.isLoading}
        period={period}
        control={<PeriodControl value={period} onApply={setPeriod} />}
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <KPI
          label="GMV · 30d"
          value={isLoading ? '…' : fmtMoney(kpis?.gmv30d ?? 0)}
          hint="confirmed + completed"
        />
        <KPI
          label="Bookings · 7d"
          value={isLoading ? '…' : String(kpis?.bookings7d ?? 0)}
          hint="all statuses"
        />
        <KPI
          label="Pending approvals"
          value={isLoading ? '…' : String(kpis?.pendingApprovals ?? 0)}
          hint="cars awaiting review"
        />
        <KPI
          label="Flagged items"
          value={isLoading ? '…' : String(kpis?.flaggedItems ?? 0)}
          hint="needs operator look"
        />
        <KPI
          label="Active admins"
          value={isLoading ? '…' : String(kpis?.activeUsers ?? 0)}
          hint="backoffice staff"
        />
        <KPI
          label="Crew"
          value={isLoading ? '…' : String(kpis?.vendors ?? 0)}
          hint="role: vendor"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 lg:col-span-2">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
                GMV by day
              </div>
              <div className="mt-1 text-[26px] font-semibold tabular-nums">
                {fmtMoney(totalGmv)}{' '}
                <span className="text-[13px] font-normal text-[color:var(--bo-mute)]">
                  last 14 days
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[12px] text-[color:var(--bo-mute)]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[color:var(--bo-accent)]" />
                GMV
              </span>
            </div>
          </div>
          <div className="mt-5 h-[200px]">
            <BarChart
              data={buckets as unknown as Array<Record<string, string | number>>}
              keys={[{ key: 'total', color: 'var(--bo-accent)' }]}
              labelKey="day"
            />
          </div>
        </div>
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
            Daily volume
          </div>
          <div className="mt-1 text-[26px] font-semibold tabular-nums">{totalBookings}</div>
          <div className="mt-4">
            <Sparkline data={sparkValues} color="var(--bo-accent)" height={80} />
          </div>
        </div>
      </div>

      {/* Queues + audit */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-semibold">Needs a human</div>
            <Link
              to={'/bookings' as never}
              className="text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
            >
              Open queues →
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {QUEUES.map((q) => (
              <Link
                key={q.label}
                to={q.to as never}
                className="rounded-lg border border-[color:var(--bo-line)] p-3 hover:border-[color:var(--bo-mute)]"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11.5px] text-[color:var(--bo-mute)]">{q.label}</div>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: q.accent }} />
                </div>
                <div className="mt-2 text-[22px] font-semibold tabular-nums">
                  {isLoading ? '…' : q.read(queues)}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="text-[14px] font-semibold">Audit log</div>
          <ul className="mt-3 space-y-3">
            {(data?.auditLog ?? []).slice(0, 8).map((row) => (
              <li key={row.id} className="flex items-start gap-3 text-[12.5px]">
                <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--bo-accent)]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[color:var(--bo-ink)]">
                    <span className="font-semibold">{row.actorEmail ?? 'system'}</span> ·{' '}
                    {row.action}
                  </div>
                  <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
                    {row.entityType} #{row.entityId}
                  </div>
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--bo-mute)]">
                  {relTime(row.createdAt)}
                </span>
              </li>
            ))}
            {!isLoading && (data?.auditLog ?? []).length === 0 ? (
              <li className="text-[12px] text-[color:var(--bo-mute)]">No audit entries yet.</li>
            ) : null}
          </ul>
        </div>
      </div>

      {/* Recent bookings */}
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="flex items-center justify-between">
          <div className="text-[14px] font-semibold">Recent bookings</div>
          <Link
            to={'/bookings' as never}
            className="text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            All bookings →
          </Link>
        </div>
        <ul className="mt-3 divide-y divide-[color:var(--bo-line)]">
          {(data?.recentBookings ?? []).slice(0, 8).map((b) => (
            <li key={b.id} className="flex items-center gap-3 py-2.5 text-[13px]">
              <Avatar name={b.customerName} size={28} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{b.customerName}</div>
                <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
                  {b.carSlug ?? '—'} · {b.reference} · {new Date(b.dateFrom).toLocaleDateString()}
                </div>
              </div>
              <div className="text-right text-[12.5px] tabular-nums">
                {fmtMoney(b.total, b.currency || 'EUR')}
              </div>
              <StatusPill status={b.status} />
            </li>
          ))}
          {!isLoading && (data?.recentBookings ?? []).length === 0 ? (
            <li className="py-4 text-center text-[12px] text-[color:var(--bo-mute)]">
              No bookings yet.
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}
